/*
 * W1 Sports — the sport tabs on the routine section, and getting the hero
 * video to play inside WeChat.
 *
 * Every sport's three cards are already in the markup; this only moves the
 * hidden attribute and the aria state. Nothing loads when a tab is pressed, so
 * there is no flash of a half-swapped card, the copy for all five sports is
 * there for a crawler to read, and with the script blocked the first sport
 * still shows rather than the section rendering empty.
 *
 * Runs once per section. The theme editor re-renders a single section without
 * reloading the page, so the same wiring has to survive being asked twice.
 */
(function () {
  'use strict';

  // addEventListener's options object is read as useCapture by older webviews,
  // which would leave the listener attached. Removing it by hand works everywhere.
  function once(target, type, fn) {
    function handler() {
      target.removeEventListener(type, handler);
      fn();
    }
    target.addEventListener(type, handler);
  }

  function wire(root) {
    if (root.dataset.w1spReady === 'true') return;
    root.dataset.w1spReady = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-w1sp-tab]'));
    if (tabs.length < 2) return;

    function select(tab) {
      tabs.forEach(function (candidate) {
        var on = candidate === tab;
        candidate.setAttribute('aria-selected', on ? 'true' : 'false');
        candidate.tabIndex = on ? 0 : -1;

        // getElementById rather than a selector, because a section id in the
        // theme editor looks like `template--17...__hero` and would have to be
        // escaped before it could go in a query.
        var panel = document.getElementById(candidate.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }

    root.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-w1sp-tab]');
      if (tab && tabs.indexOf(tab) !== -1) select(tab);
    });

    root.addEventListener('keydown', function (event) {
      var index = tabs.indexOf(document.activeElement);
      if (index === -1) return;

      var next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = tabs[(index + 1) % tabs.length];
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = tabs[(index - 1 + tabs.length) % tabs.length];
      } else if (event.key === 'Home') {
        next = tabs[0];
      } else if (event.key === 'End') {
        next = tabs[tabs.length - 1];
      }
      if (!next) return;

      event.preventDefault();
      next.focus();
      select(next);
    });
  }

  /*
   * WeChat will not honour autoplay on its own, muted or not, and the markup
   * attributes cannot say so. Three attempts, cheapest first: straight away
   * for every ordinary browser, again once WeChat's bridge comes up, and
   * failing both, on the first touch anywhere — a gesture no webview refuses.
   *
   * A rejected play() is expected rather than exceptional, so its promise is
   * swallowed; left alone it logs an unhandled rejection on every load.
   */
  function wireVideo(video) {
    if (video.dataset.w1spReady === 'true') return;
    video.dataset.w1spReady = 'true';

    // Some webviews consult the property and ignore the attribute, and an
    // unmuted video is refused outright.
    video.muted = true;

    // play() resolving proves only that the request was accepted. On a slow
    // link it can resolve and then sit there buffering, so the frame that
    // actually arrives is what counts as started.
    var started = false;
    video.addEventListener('playing', function () {
      started = true;
    });

    function attempt() {
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    }

    attempt();

    // A deferred script usually runs after WeixinJSBridgeReady has fired, so
    // the bridge being present already is the common case, not the fallback.
    if (window.WeixinJSBridge) {
      attempt();
    } else {
      once(document, 'WeixinJSBridgeReady', attempt);
    }

    once(document, 'touchstart', attempt);

    // Nothing has played by now, either because the webview refused or because
    // the file is still coming down. A video frozen on its poster reads as
    // broken, so hand over the browser's own controls: tapping those is a
    // gesture no webview turns down, and it shows a progress spinner instead
    // of a dead picture while the rest of the file arrives.
    setTimeout(function () {
      if (!started) video.controls = true;
    }, 4000);
  }

  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-w1sp-routine]').forEach(wire);
    root.querySelectorAll('video[data-w1sp-autoplay]').forEach(wireVideo);
  }

  boot();
  document.addEventListener('DOMContentLoaded', function () {
    boot();
  });
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
